import { Bell, TrendingUp } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyState } from '@/components/shared/EmptyState';
import { GapSignCell } from '@/components/shared/GapSignCell';
import { MetricCard } from '@/components/shared/MetricCard';
import { useCurrentUser } from '@/lib/auth/hooks';
import { formatAmount } from '@/lib/utils';

import { useLatestGap } from '@/features/alm/api';
import type { GapBucket } from '@/features/alm/types';
import { useAlerts } from '@/features/alerts/api';
import { useLatestEve } from '@/features/eve/api';
import type { IrrbbScenarioCode } from '@/features/eve/types';
import { SCENARIO_LABELS } from '@/features/eve/types';
import { useLatestFtp } from '@/features/ftp/api';
import { useLatestLcr } from '@/features/liquidity/api';
import { useLatestNim } from '@/features/nim/api';

export function DashboardPage() {
  const user  = useCurrentUser();
  const today = new Date().toISOString().slice(0, 10);

  const gapQuery   = useLatestGap(today);
  const lcrQuery   = useLatestLcr(today);
  const nimQuery   = useLatestNim(today);
  const eveQuery   = useLatestEve(today);
  const ftpQuery   = useLatestFtp(today);
  const alertQuery = useAlerts(false);   // unacknowledged only

  const lcr  = lcrQuery.data?.lcrRatio        ?? null;
  const nsfr = lcrQuery.data?.nsfrRatio       ?? null;
  const nim  = nimQuery.data?.nimBaseline      ?? null;
  const eve  = eveQuery.data?.worstCaseEvePct  ?? null;

  const lcrPct  = lcr  !== null ? parseFloat(lcr)  * 100 : null;
  const nsfrPct = nsfr !== null ? parseFloat(nsfr) * 100 : null;
  const nimPct  = nim  !== null ? parseFloat(nim)  * 100 : null;
  const evePct  = eve  !== null ? parseFloat(eve)  * 100 : null;

  const unreadAlerts  = alertQuery.data?.unacknowledgedCount ?? 0;
  const engineVersion = gapQuery.data?.engineVersion
    ?? eveQuery.data?.engineVersion
    ?? nimQuery.data?.engineVersion
    ?? null;

  const allEmpty = gapQuery.isError && lcrQuery.isError && nimQuery.isError && eveQuery.isError;

  return (
    <div className="space-y-6">
      {/* ── Page heading ─────────────────────────────────────────────────── */}
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-lg font-semibold tracking-tight">Dashboard</h1>
          <Badge variant="secondary">{user?.tenant_id ?? '—'}</Badge>
          <Badge variant="info">{today}</Badge>
        </div>
        <p className="mt-1 text-[12px] text-muted-foreground">
          Latest calculated results
          {engineVersion && (
            <> · engine <code className="rounded bg-muted px-1 py-0.5 text-[11px]">{engineVersion}</code></>
          )}
        </p>
      </div>

      {/* ── KPI row ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <MetricCard
          label="LCR"
          value={lcrPct !== null ? `${lcrPct.toFixed(1)}%` : '—'}
          delta={
            lcrPct !== null
              ? lcrPct >= 100 ? 'Above minimum' : 'Below minimum'
              : 'No data yet'
          }
          deltaDirection={lcrPct !== null ? (lcrPct >= 100 ? 'up' : 'down') : 'neutral'}
        />
        <MetricCard
          label="NSFR"
          value={
            nsfrPct !== null
              ? `${nsfrPct.toFixed(1)}%`
              : lcrQuery.data?.nsfrDataAvailable === false ? 'N/A' : '—'
          }
          delta={
            nsfrPct !== null
              ? nsfrPct >= 100 ? 'Above minimum' : 'Below minimum'
              : lcrQuery.data?.nsfrDataAvailable === false ? 'Gold data pending' : 'No data yet'
          }
          deltaDirection={nsfrPct !== null ? (nsfrPct >= 100 ? 'up' : 'down') : 'neutral'}
        />
        <MetricCard
          label="NIM (baseline)"
          value={nimPct !== null ? `${nimPct.toFixed(2)}%` : '—'}
          delta={nimQuery.data?.degradedMode ? 'Degraded mode' : nimPct !== null ? 'Curve-aware' : 'No data yet'}
          deltaDirection={nimQuery.data?.degradedMode ? 'down' : 'neutral'}
        />
        <MetricCard
          label="EVE worst-case"
          value={
            evePct !== null ? (
              <span className={evePct < -15 ? 'text-cx-danger' : evePct < -10 ? 'text-cx-warning' : undefined}>
                {evePct.toFixed(1)}%
              </span>
            ) : '—'
          }
          delta={
            evePct !== null
              ? evePct < -15 ? 'Supervisory outlier' : evePct < -10 ? 'Elevated' : 'Within limits'
              : 'No data yet'
          }
          deltaDirection={evePct !== null ? (evePct < -15 ? 'down' : evePct < -10 ? 'down' : 'neutral') : 'neutral'}
        />
        {/* Alerts KPI — red when any unacknowledged alerts exist */}
        <MetricCard
          label="Active alerts"
          value={
            alertQuery.isLoading ? '…' : (
              <span className={unreadAlerts > 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}>
                {unreadAlerts}
              </span>
            )
          }
          delta={
            unreadAlerts > 0
              ? `${unreadAlerts} need review`
              : 'All clear'
          }
          deltaDirection={unreadAlerts > 0 ? 'down' : 'up'}
        />
      </div>

      {/* ── Main panels ──────────────────────────────────────────────────── */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Liquidity gap summary */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-[13px] font-medium">Liquidity gap — flow mode</CardTitle>
              {gapQuery.data && (
                <Badge variant="teal">{gapQuery.data.calculationDate}</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {gapQuery.isLoading && (
              <div className="space-y-2">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-8 animate-pulse rounded bg-muted" />
                ))}
              </div>
            )}
            {(gapQuery.isError || gapQuery.data === null) && !gapQuery.isLoading && (
              <EmptyState
                icon={<TrendingUp />}
                title="No gap results yet"
                description="Run a gap analysis from ALM Analysis → Gap analysis to see results here."
              />
            )}
            {gapQuery.data?.buckets && gapQuery.data.buckets.length > 0 && (
              <div>
                <div className="grid grid-cols-4 gap-0 pb-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                  <span>Bucket</span>
                  <span className="text-right">Assets</span>
                  <span className="text-right">Liabilities</span>
                  <span className="text-right">Net gap</span>
                </div>
                {gapQuery.data.buckets.slice(0, 6).map((b: GapBucket) => (
                  <div
                    key={b.bucketIndex}
                    className="grid grid-cols-4 gap-0 border-t border-border py-2 text-[12px]"
                  >
                    <span className="text-muted-foreground">{b.label}</span>
                    <span className="text-right font-mono text-[11px]">{formatAmount(b.assetsInflow)}</span>
                    <span className="text-right font-mono text-[11px]">{formatAmount(b.liabilitiesOutflow)}</span>
                    <span className="text-right">
                      <GapSignCell value={b.netGap} />
                    </span>
                  </div>
                ))}
                {gapQuery.data.buckets.length > 6 && (
                  <p className="pt-2 text-[11px] text-muted-foreground">
                    + {gapQuery.data.buckets.length - 6} more buckets — see full report
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* IRRBB status */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-[13px] font-medium">IRRBB status</CardTitle>
              <Badge variant="info">BCBS 368</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {eveQuery.isLoading && (
              <div className="space-y-2">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-8 animate-pulse rounded bg-muted" />
                ))}
              </div>
            )}
            {(eveQuery.isError || (!eveQuery.isLoading && !eveQuery.data)) && (
              <EmptyState
                icon={<TrendingUp />}
                title="No EVE results yet"
                description="Run an EVE analysis from the IRRBB menu."
              />
            )}
            {eveQuery.data && (
              <>
                <IrrbbRow
                  label={SCENARIO_LABELS['PARALLEL_UP_200']}
                  rawValue={eveQuery.data.eveParallelUp}
                  pv={eveQuery.data.portfolioPv}
                  threshold={-15}
                />
                <IrrbbRow
                  label="Worst-case EVE"
                  rawValue={eveQuery.data.worstCaseEve}
                  pv={eveQuery.data.portfolioPv}
                  threshold={-15}
                  worstCaseScenario={eveQuery.data.worstCaseScenario}
                />
                {nimQuery.data && (
                  <div>
                    <div className="mb-1 flex items-center justify-between text-[11px]">
                      <span className="text-muted-foreground">NIM delta (stressed − baseline)</span>
                      <GapSignCell value={(parseFloat(nimQuery.data.nimDelta) * 100).toFixed(2)} unit="pp" />
                    </div>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-cx-blue transition-all"
                        style={{ width: `${Math.min(Math.abs(parseFloat(nimQuery.data.nimDelta)) * 100 / 2, 100)}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Supervisory outlier flag */}
                <div className="flex items-center gap-2 border-t border-border pt-2">
                  <span
                    className={`h-2 w-2 rounded-full ${evePct !== null && evePct < -15 ? 'bg-cx-danger' : 'bg-cx-warning'}`}
                  />
                  <span className="text-[11px] text-muted-foreground">Supervisory outlier flag:</span>
                  <Badge variant={evePct !== null && evePct < -15 ? 'danger' : 'warning'}>
                    {evePct === null ? 'No data' : evePct < -15 ? 'Outlier' : 'Watch'}
                  </Badge>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── FTP summary panel ────────────────────────────────────────────── */}
      {(ftpQuery.data || ftpQuery.isLoading) && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-[13px] font-medium">Fund Transfer Pricing — net position</CardTitle>
              {ftpQuery.data && (
                <Badge variant="teal">{ftpQuery.data.calculationDate}</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {ftpQuery.isLoading && (
              <div className="flex gap-6">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-10 w-36 animate-pulse rounded bg-muted" />
                ))}
              </div>
            )}
            {ftpQuery.data && (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <FtpMetric
                  label="Asset FTP cost"
                  value={ftpQuery.data.totalAssetFtpCost}
                  negative
                />
                <FtpMetric
                  label="Liability FTP benefit"
                  value={ftpQuery.data.totalLiabilityFtpBenefit}
                />
                <FtpMetric
                  label="Net FTP position"
                  value={ftpQuery.data.netFtpPosition}
                  highlight
                />
                <FtpMetric
                  label="Coverage ratio"
                  value={(parseFloat(ftpQuery.data.coverageRatio) * 100).toFixed(1) + '%'}
                  raw
                />
              </div>
            )}
            {ftpQuery.data?.degradedMode && (
              <p className="mt-2 text-[11px] text-amber-600 dark:text-amber-400">
                ⚠ Degraded mode — some cashflows used placeholder rates. Upload a validated yield-curve snapshot.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Alerts summary ───────────────────────────────────────────────── */}
      {unreadAlerts > 0 && (
        <Card className="border-red-200 dark:border-red-900">
          <CardContent className="flex items-center gap-3 py-3">
            <Bell className="h-4 w-4 shrink-0 text-red-500" />
            <p className="text-sm">
              <span className="font-medium text-red-600 dark:text-red-400">
                {unreadAlerts} unacknowledged alert{unreadAlerts > 1 ? 's' : ''}
              </span>
              {' '}require review — go to{' '}
              <a href="/alm/alerts" className="underline underline-offset-2 hover:text-foreground">
                Monitoring → Alerts
              </a>.
            </p>
          </CardContent>
        </Card>
      )}

      {/* ── Getting started — shown only when truly empty ─────────────────── */}
      {allEmpty && (
        <Card>
          <CardContent className="py-5 text-[12px] text-muted-foreground">
            No calculations have been run yet for <strong>{user?.tenant_id}</strong>.
            Use the sidebar to navigate to any analysis module and click{' '}
            <strong>Run</strong> to start. Results appear here automatically once available.
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── helpers ───────────────────────────────────────────────────────────────────

function IrrbbRow({
  label,
  rawValue,
  pv,
  threshold,
  worstCaseScenario,
}: {
  label: string;
  rawValue: string;
  pv: string;
  threshold: number | null;
  worstCaseScenario?: IrrbbScenarioCode | null;
}) {
  const pvNum  = parseFloat(pv);
  const eveNum = parseFloat(rawValue);
  const pct    = pvNum !== 0 ? (eveNum / Math.abs(pvNum)) * 100 : 0;
  const breached = threshold !== null && pct < threshold;
  const barPct = Math.min(Math.abs(pct) / 25 * 100, 100);

  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-[11px]">
        <span className="text-muted-foreground">
          {label}
          {worstCaseScenario && (
            <span className="ml-1 text-muted-foreground/60">({SCENARIO_LABELS[worstCaseScenario]})</span>
          )}
        </span>
        <GapSignCell value={pct.toFixed(1)} unit="%" />
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={`h-full rounded-full transition-all ${
            breached ? 'bg-cx-danger' : Math.abs(pct) > 10 ? 'bg-cx-warning' : 'bg-cx-blue'
          }`}
          style={{ width: `${barPct}%` }}
        />
      </div>
    </div>
  );
}

function FtpMetric({
  label, value, negative = false, highlight = false, raw = false,
}: {
  label: string;
  value: string;
  negative?: boolean;
  highlight?: boolean;
  raw?: boolean;
}) {
  const display = raw ? value : formatAmount(value);
  const num = raw ? null : parseFloat(value);
  const colour = highlight
    ? (num !== null && num >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400')
    : negative
    ? 'text-red-500 dark:text-red-400'
    : undefined;

  return (
    <div>
      <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`mt-0.5 text-[13px] font-semibold font-mono ${colour ?? ''}`}>{display}</p>
    </div>
  );
}
